import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import PropTypes from "prop-types";


function normalizeMoneyInput(
  value
) {
  const normalized =
    String(
      value ?? ""
    )
      .trim()
      .replace(
        ",",
        "."
      );

  if (!normalized) {
    return null;
  }

  const amount =
    Number(
      normalized
    );

  if (
    !Number.isFinite(
      amount
    )
    || amount < 0
  ) {
    return null;
  }

  return amount;
}


function resolveSessionUuid(
  session
) {
  const candidate =
    session?.cash_session_uuid
    ?? session?.cashSessionUuid
    ?? session?.uuid;

  if (
    typeof candidate !== "string"
    || candidate.trim() === ""
  ) {
    return null;
  }

  return candidate.trim();
}


function resolveActiveSession(
  snapshot
) {
  if (
    !snapshot
    || typeof snapshot !== "object"
  ) {
    return null;
  }

  if (
    snapshot.is_open === false
    || snapshot.isOpen === false
  ) {
    return null;
  }

  const nested =
    snapshot.cash_session
    ?? snapshot.cashSession
    ?? snapshot.session;

  if (
    nested
    && typeof nested === "object"
  ) {
    return nested;
  }

  if (
    resolveSessionUuid(
      snapshot
    )
  ) {
    return snapshot;
  }

  return null;
}


function normalizeHistory(
  value
) {
  if (Array.isArray(value)) {
    return value;
  }

  if (
    value
    && Array.isArray(
      value.sessions
    )
  ) {
    return value.sessions;
  }

  return [];
}


function formatMoney(
  value
) {
  const amount =
    Number(
      value ?? 0
    );

  if (
    !Number.isFinite(
      amount
    )
  ) {
    return "—";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style:
        "currency",

      currency:
        "EUR",
    }
  ).format(
    amount
  );
}


export default function RetailPOSCashSessionWorkspace({
  runtime,
  context,
}) {
  const mountedRef =
    useRef(true);

  const [
    currentSnapshot,
    setCurrentSnapshot,
  ] =
    useState(null);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    actionPending,
    setActionPending,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState(null);

  const [
    openingCash,
    setOpeningCash,
  ] =
    useState("");

  const [
    countedCash,
    setCountedCash,
  ] =
    useState("");

  const [
    bankDeposit,
    setBankDeposit,
  ] =
    useState("");

  const [
    closureComment,
    setClosureComment,
  ] =
    useState("");

  const [
    history,
    setHistory,
  ] =
    useState([]);

  const [
    historyLoading,
    setHistoryLoading,
  ] =
    useState(false);

  const [
    report,
    setReport,
  ] =
    useState(null);

  const [
    reportLoading,
    setReportLoading,
  ] =
    useState(false);


  useEffect(
    () => {
      mountedRef.current =
        true;

      return () => {
        mountedRef.current =
          false;
      };
    },
    []
  );


  const activeSession =
    useMemo(
      () =>
        resolveActiveSession(
          currentSnapshot
        ),
      [
        currentSnapshot,
      ]
    );


  const sessionUuid =
    resolveSessionUuid(
      activeSession
    );


  const refreshCurrentSession =
    useCallback(
      async () => {
        setLoading(true);
        setError(null);

        try {
          const snapshot =
            await runtime
              .resolveCurrentSession(
                context
              );

          if (!mountedRef.current) {
            return null;
          }

          setCurrentSnapshot(
            snapshot
            ?? null
          );

          return snapshot;
        } catch (cause) {
          if (!mountedRef.current) {
            return null;
          }

          setCurrentSnapshot(
            null
          );

          setError(
            cause?.message
            ?? "Impossible de charger la session de caisse."
          );

          return null;
        } finally {
          if (mountedRef.current) {
            setLoading(false);
          }
        }
      },
      [
        context,
        runtime,
      ]
    );


  useEffect(
    () => {
      refreshCurrentSession();
    },
    [
      refreshCurrentSession,
    ]
  );


  async function handleOpenSession(
    event
  ) {
    event.preventDefault();

    if (actionPending) {
      return;
    }

    const amount =
      normalizeMoneyInput(
        openingCash
      );

    if (amount === null) {
      setError(
        "Le fond de caisse doit être un montant valide."
      );

      return;
    }

    setActionPending(true);
    setError(null);

    try {
      await runtime.openSession(
        {
          opening_cash:
            amount,
        },
        context
      );

      if (!mountedRef.current) {
        return;
      }

      setOpeningCash("");

      await refreshCurrentSession();
    } catch (cause) {
      if (!mountedRef.current) {
        return;
      }

      setError(
        cause?.message
        ?? "Impossible d'ouvrir la session de caisse."
      );
    } finally {
      if (mountedRef.current) {
        setActionPending(false);
      }
    }
  }


  async function handleCloseSession(
    event
  ) {
    event.preventDefault();

    if (
      actionPending
      || !sessionUuid
    ) {
      return;
    }

    const normalizedCountedCash =
      normalizeMoneyInput(
        countedCash
      );

    const normalizedBankDeposit =
      bankDeposit.trim() === ""
        ? 0
        : normalizeMoneyInput(
            bankDeposit
          );

    if (
      normalizedCountedCash === null
      || normalizedBankDeposit === null
    ) {
      setError(
        "Les montants de clôture sont invalides."
      );

      return;
    }

    setActionPending(true);
    setError(null);

    try {
      await runtime.closeSession(
        sessionUuid,
        {
          counted_cash:
            normalizedCountedCash,

          bank_deposit:
            normalizedBankDeposit,

          closure_comment:
            closureComment.trim(),
        },
        context
      );

      if (!mountedRef.current) {
        return;
      }

      setCountedCash("");
      setBankDeposit("");
      setClosureComment("");
      setReport(null);

      await refreshCurrentSession();
    } catch (cause) {
      if (!mountedRef.current) {
        return;
      }

      setError(
        cause?.message
        ?? "Impossible de clôturer la session de caisse."
      );
    } finally {
      if (mountedRef.current) {
        setActionPending(false);
      }
    }
  }


  async function handleLoadHistory() {
    if (historyLoading) {
      return;
    }

    setHistoryLoading(true);
    setError(null);

    try {
      const result =
        await runtime
          .getSessionHistory(
            context
          );

      if (!mountedRef.current) {
        return;
      }

      setHistory(
        normalizeHistory(
          result
        )
      );
    } catch (cause) {
      if (!mountedRef.current) {
        return;
      }

      setHistory([]);

      setError(
        cause?.message
        ?? "Impossible de charger l'historique de caisse."
      );
    } finally {
      if (mountedRef.current) {
        setHistoryLoading(false);
      }
    }
  }


  async function handleLoadReport(
    cashSessionUuid
  ) {
    if (
      reportLoading
      || !cashSessionUuid
    ) {
      return;
    }

    setReportLoading(true);
    setError(null);

    try {
      const result =
        await runtime
          .getSessionReport(
            cashSessionUuid,
            context
          );

      if (!mountedRef.current) {
        return;
      }

      setReport(
        result
        ?? null
      );
    } catch (cause) {
      if (!mountedRef.current) {
        return;
      }

      setReport(null);

      setError(
        cause?.message
        ?? "Impossible de charger le rapport Z."
      );
    } finally {
      if (mountedRef.current) {
        setReportLoading(false);
      }
    }
  }


  return (
    <section
      className="retail-pos-cash-session"
      data-testid="retail-pos-cash-session-workspace"
      aria-labelledby="retail-pos-cash-session-title"
    >
      <header
        className="retail-pos-cash-session__header"
      >
        <div>
          <p
            className="retail-pos-cash-session__eyebrow"
          >
            Caisse
          </p>

          <h3
            id="retail-pos-cash-session-title"
          >
            Session de caisse
          </h3>
        </div>

        <button
          type="button"
          onClick={
            refreshCurrentSession
          }
          disabled={
            loading
            || actionPending
          }
          data-testid="retail-pos-cash-session-refresh"
        >
          Actualiser
        </button>
      </header>


      {loading && (
        <p
          role="status"
          data-testid="retail-pos-cash-session-loading"
        >
          Chargement de la session de caisse…
        </p>
      )}


      {error && (
        <p
          role="alert"
          className="retail-pos-cash-session__error"
          data-testid="retail-pos-cash-session-error"
        >
          {error}
        </p>
      )}


      {!loading
        && !activeSession
        && (
          <form
            className="retail-pos-cash-session__form"
            onSubmit={
              handleOpenSession
            }
            data-testid="retail-pos-cash-session-open-form"
          >
            <p>
              Aucune session de caisse active pour cette activité.
            </p>

            <label>
              Fond de caisse
              <input
                type="text"
                inputMode="decimal"
                value={
                  openingCash
                }
                onChange={
                  (event) =>
                    setOpeningCash(
                      event.target.value
                    )
                }
                data-testid="retail-pos-cash-session-opening-cash"
              />
            </label>

            <button
              type="submit"
              disabled={
                actionPending
              }
              data-testid="retail-pos-cash-session-open"
            >
              {actionPending
                ? "Ouverture…"
                : "Ouvrir la caisse"}
            </button>
          </form>
        )}


      {!loading
        && activeSession
        && (
          <>
            <dl
              className="retail-pos-cash-session__summary"
              data-testid="retail-pos-cash-session-active"
            >
              <div>
                <dt>Session</dt>
                <dd>
                  {sessionUuid}
                </dd>
              </div>

              <div>
                <dt>Fond initial</dt>
                <dd>
                  {formatMoney(
                    activeSession
                      ?.opening_cash
                    ?? activeSession
                      ?.openingCash
                  )}
                </dd>
              </div>

              <div>
                <dt>Espèces attendues</dt>
                <dd
                  data-testid="retail-pos-cash-session-expected-cash"
                >
                  {formatMoney(
                    activeSession
                      ?.expected_cash
                    ?? activeSession
                      ?.expectedCash
                  )}
                </dd>
              </div>

              {(
                activeSession
                  ?.cash_difference
                ?? activeSession
                  ?.cashDifference
              ) !== undefined && (
                <div>
                  <dt>Écart</dt>
                  <dd
                    data-testid="retail-pos-cash-session-difference"
                  >
                    {formatMoney(
                      activeSession
                        ?.cash_difference
                      ?? activeSession
                        ?.cashDifference
                    )}
                  </dd>
                </div>
              )}
            </dl>


            <form
              className="retail-pos-cash-session__form"
              onSubmit={
                handleCloseSession
              }
              data-testid="retail-pos-cash-session-close-form"
            >
              <label>
                Espèces comptées
                <input
                  type="text"
                  inputMode="decimal"
                  value={
                    countedCash
                  }
                  onChange={
                    (event) =>
                      setCountedCash(
                        event.target.value
                      )
                  }
                  data-testid="retail-pos-cash-session-counted-cash"
                />
              </label>

              <label>
                Dépôt banque
                <input
                  type="text"
                  inputMode="decimal"
                  value={
                    bankDeposit
                  }
                  onChange={
                    (event) =>
                      setBankDeposit(
                        event.target.value
                      )
                  }
                  data-testid="retail-pos-cash-session-bank-deposit"
                />
              </label>

              <label>
                Commentaire
                <textarea
                  value={
                    closureComment
                  }
                  onChange={
                    (event) =>
                      setClosureComment(
                        event.target.value
                      )
                  }
                  data-testid="retail-pos-cash-session-comment"
                />
              </label>

              <button
                type="submit"
                disabled={
                  actionPending
                }
                data-testid="retail-pos-cash-session-close"
              >
                {actionPending
                  ? "Clôture…"
                  : "Clôturer la caisse"}
              </button>
            </form>
          </>
        )}


      <div
        className="retail-pos-cash-session__history-actions"
      >
        <button
          type="button"
          onClick={
            handleLoadHistory
          }
          disabled={
            historyLoading
          }
          data-testid="retail-pos-cash-session-history-load"
        >
          {historyLoading
            ? "Chargement…"
            : "Historique de caisse"}
        </button>
      </div>


      {history.length > 0 && (
        <div
          className="retail-pos-cash-session__history"
          data-testid="retail-pos-cash-session-history"
        >
          {history.map(
            (
              item,
              index
            ) => {
              const cashSessionUuid =
                resolveSessionUuid(
                  item
                );

              return (
                <div
                  key={
                    cashSessionUuid
                    ?? `cash-session-${index}`
                  }
                >
                  <span>
                    {cashSessionUuid
                      ?? "Session"}
                  </span>

                  {cashSessionUuid && (
                    <button
                      type="button"
                      onClick={
                        () =>
                          handleLoadReport(
                            cashSessionUuid
                          )
                      }
                      disabled={
                        reportLoading
                      }
                    >
                      Rapport Z
                    </button>
                  )}
                </div>
              );
            }
          )}
        </div>
      )}


      {report && (
        <div
          className="retail-pos-cash-session__report"
          data-testid="retail-pos-cash-session-report"
        >
          <strong>
            Rapport Z autoritaire chargé
          </strong>

          {report
            ?.cash_control
            ?.expected_cash
            !== undefined && (
            <p>
              Espèces attendues :
              {" "}
              {formatMoney(
                report
                  .cash_control
                  .expected_cash
              )}
            </p>
          )}

          {report
            ?.cash_control
            ?.cash_difference
            !== undefined && (
            <p>
              Écart :
              {" "}
              {formatMoney(
                report
                  .cash_control
                  .cash_difference
              )}
            </p>
          )}
        </div>
      )}
    </section>
  );
}


RetailPOSCashSessionWorkspace.propTypes = {
  runtime:
    PropTypes.shape({
      openSession:
        PropTypes.func.isRequired,

      resolveCurrentSession:
        PropTypes.func.isRequired,

      closeSession:
        PropTypes.func.isRequired,

      getSessionHistory:
        PropTypes.func.isRequired,

      getSessionReport:
        PropTypes.func.isRequired,
    }).isRequired,

  context:
    PropTypes.shape({
      companyUuid:
        PropTypes.string.isRequired,

      activityUuid:
        PropTypes.string.isRequired,
    }).isRequired,
};
